import React, { useState } from 'react';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { X, CreditCard, Loader } from 'lucide-react';
import { useApi } from '../hooks/useApi';
import toast from 'react-hot-toast';

const CardForm = ({ onSuccess, onCancel, user }) => {
  const stripe = useStripe();
  const elements = useElements();
  const api = useApi();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Check if Stripe is properly configured
  if (!stripe || !elements) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-gray-900 border border-white/10 rounded-lg p-6 max-w-md w-full mx-4">
          <div className="text-center">
            <h2 className="text-xl font-semibold text-white mb-4">Stripe Not Configured</h2>
            <p className="text-gray-400 mb-6">
              Stripe is not properly configured. Please contact your administrator.
            </p>
            <button
              onClick={onCancel}
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Get the card element
      const cardElement = elements.getElement(CardElement);

      if (!cardElement) {
        throw new Error('Card element not found');
      }

      // Create payment method
      const { error: stripeError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
        billing_details: {
          email: user.email,
          name: `${user.firstName} ${user.lastName}`,
        },
      });

      if (stripeError) {
        throw new Error(stripeError.message);
      }

      // Attach payment method to customer
      const response = await api.post('/api/v1/billing/attach-payment-method', {
        paymentMethodId: paymentMethod.id
      });

      if (response.data.success) {
        toast.success('Payment method added successfully');
        onSuccess();
      } else {
        throw new Error(response.data.message || 'Failed to add payment method');
      }

    } catch (err) {
      console.error('Error adding payment method:', err);
      setError(err.message);
      toast.error(err.message || 'Failed to add payment method');
    } finally {
      setLoading(false);
    }
  };

  const cardElementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: '#ffffff',
        '::placeholder': {
          color: '#aab7c4',
        },
        backgroundColor: 'transparent',
      },
      invalid: {
        color: '#fa755a',
        iconColor: '#fa755a',
      },
    },
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-gray-900 border border-white/10 rounded-lg p-6 max-w-md w-full mx-4">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-white">Add Payment Method</h2>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-white"
            disabled={loading}
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-white mb-2">
              Card Information
            </label>
            <div className="p-3 bg-white/5 border border-white/10 rounded-md">
              <CardElement options={cardElementOptions} />
            </div>
            {error && (
              <p className="text-red-400 text-sm mt-2">{error}</p>
            )}
          </div>

          <div className="flex items-center space-x-2 text-sm text-gray-400">
            <CreditCard className="h-4 w-4" />
            <span>Your card information is secure and encrypted</span>
          </div>

          <div className="flex space-x-3">
            <button
              type="button"
              onClick={onCancel}
              disabled={loading}
              className="px-4 py-2 text-gray-400 hover:text-white disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!stripe || loading}
              className="flex-1 px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {loading ? (
                <>
                  <Loader className="h-4 w-4 animate-spin mr-2" />
                  Adding...
                </>
              ) : (
                'Add Card'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CardForm;
